import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon, SystemBanner } from '@etnas/design-system';
import { breakGlassApi, platformApi, type BreakGlassGrant } from '@/api';
import { qk } from '@/lib/queryKeys';
import { fmtTime } from '@/lib/format';
import { useConnectivity } from './connectivity';
import { useAuth } from '../auth';
import { useToast } from '@/components/Toaster';

/**
 * System bar (zone 2): appears only when something is abnormal — training data, downtime, AI paused,
 * support session, break-glass (time-boxed, with End access), and offline.
 */
export function SystemBanners({ workforce = true }: { workforce?: boolean }) {
  const { me } = useAuth();
  // Tenant-scoped routes need a tenant context; the platform operator has none (R-125).
  const hasTenant = !!me?.tenant;
  const clinical = (me?.permissions.includes('break_glass.start') || me?.permissions.includes('breakglass.start')) ?? false;
  const banners = useQuery({ queryKey: qk.banners, queryFn: platformApi.systemBanners, enabled: hasTenant, refetchInterval: 60_000 });
  const bg = useQuery({ queryKey: qk.breakGlassActive, queryFn: breakGlassApi.active, enabled: workforce && hasTenant && clinical, refetchInterval: 30_000 });
  const online = useConnectivity();
  return (
    <div role="region" aria-label="System status">
      {!online ? <SystemBanner kind="downtime" message="Offline — changes are kept on this device. Signing, ordering and result release wait for the connection." /> : null}
      {(banners.data?.items ?? []).map((b) => (
        <SystemBanner
          key={b.id}
          kind={b.kind === 'feature-disabled' ? 'degraded' : b.kind}
          message={b.message}
          meta={b.meta ?? (b.endsAt ? `until ${fmtTime(b.endsAt)}` : undefined)}
        />
      ))}
      {(bg.data?.items ?? []).map((g) => (
        <BreakGlassBar key={g.id} grant={g} />
      ))}
    </div>
  );
}

function BreakGlassBar({ grant }: { grant: BreakGlassGrant }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);
  const end = useMutation({
    mutationFn: () => breakGlassApi.end(grant.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.breakGlassActive });
      qc.invalidateQueries({ queryKey: qk.patient(grant.patientId) });
      // ChartLayout derives restricted sections and session purpose from this decision.
      qc.invalidateQueries({ queryKey: qk.access(grant.patientId) });
      toast.success('Emergency access ended', `${grant.patientDisplay} · restricted records are hidden again`);
    },
    onError: (e) => toast.error(e),
  });
  const mins = Math.max(0, Math.round((new Date(grant.expiresAt).getTime() - now) / 60000));
  return (
    <div className="ex-sysbar ex-sysbar-critical" role="status">
      <Icon name="glass" size={14} />
      <span className="ex-sysbar-msg">
        Emergency access to {grant.patientDisplay} · ends {fmtTime(grant.expiresAt)} ({mins} min) · all views recorded
      </span>
      <button type="button" className="ex-sysbar-act" onClick={() => end.mutate()} disabled={end.isPending}>
        End access now
      </button>
    </div>
  );
}
